import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  requestError,
  requestInit,
  requestStart,
  RequestState,
  requestSuccess,
} from 'shared/auth/hooks/useRequestState';
import TrueImpactSearchBox from 'shared/design-system/components/TrueImpactSearchBox';
import { AddressValue } from './Locations';
import { LocationSuggestion, parseGoogleSuggestions } from './utils';

const googleFailedError = {
  isAxiosError: false,
  message: 'Unable to retrieve the search results.',
  name: 'Google Error',
  response: {
    status: 500,
  },
};

interface LocationInputProps {
  handleAdd: (address: AddressValue) => unknown;
  handleSuggestions: React.Dispatch<
    React.SetStateAction<RequestState<LocationSuggestion>>
  >;
  sessionToken: google.maps.places.AutocompleteSessionToken | null;
}

// locality -> city
// administrative_area_level_1 -> states/provinces
// country -> country
// const types = ['locality', 'administrative_area_level_1', 'country'];
const types = [];
let service: google.maps.places.AutocompleteService | undefined = undefined;
try {
  service = new google.maps.places.AutocompleteService();
} catch (error) {
  console.error(error);
}

const LocationInput: React.FC<LocationInputProps> = ({ handleSuggestions, sessionToken }) => {
  const [searchText, setSearchText] = useState<string>('');
  const ref = useRef<HTMLInputElement>(null);
  const sessionTokenRef = useRef(sessionToken);
  useEffect(() => {
    sessionTokenRef.current = sessionToken;
  }, [sessionToken]);
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
    },
    [setSearchText],
  );
  const handleClear = useCallback(() => {
    setSearchText('');
    ref.current?.focus();
  }, [ref, setSearchText]);
  // const onAdd = useCallback(() => {
  //   handleAdd({
  //     address: searchText,
  //   });
  //   setSearchText('');
  // }, [searchText, setSearchText]);

  const suggestions = useCallback(
    (
      suggestions: google.maps.places.AutocompletePrediction[] | null,
      status: google.maps.places.PlacesServiceStatusString,
    ) => {
      if (
        !suggestions ||
        status !== google.maps.places.PlacesServiceStatus.OK
      ) {
        handleSuggestions(requestError(googleFailedError));
        return;
      }
      const newSuggestions = parseGoogleSuggestions(suggestions);
      handleSuggestions(requestSuccess(newSuggestions));
    },
    [handleSuggestions],
  );

  useEffect(() => {
    if (searchText.length < 2) {
      handleSuggestions(requestInit());
      return;
    }
    if (service) {
      handleSuggestions(s => {
        if (s.type === 'REQUEST_SUCCESS') return s;
        return requestStart();
      });
      const timer = setTimeout(() => {
        service!.getPlacePredictions(
          { input: searchText, types, sessionToken: sessionTokenRef.current ?? undefined },
          suggestions,
        );
      }, 300);
      return () => clearTimeout(timer);
    } else {
      handleSuggestions(requestError(googleFailedError));
    }
  }, [searchText, suggestions, handleSuggestions]);

  return (
    <TrueImpactSearchBox
      inputRef={ref}
      placeholder="Search city, state, country"
      value={searchText}
      autoFocus={true}
      disabledClearButton={searchText.length === 0}
      onChange={handleChange}
      onClear={handleClear}
      className="location-search-field"
    />
  );
};

export default LocationInput;
